import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import {
    MessageCircle,
    Send,
    Paperclip,
    X,
    Download,
    Users,
    Bell,
    Image as ImageIcon,
    AtSign,
    Minus,
    FileText,
    Trash2,
    Reply,
    Eye,
    Save,
    Loader2,
    AlertTriangle,
    CheckCircle2
} from 'lucide-react';

const AUTO_CLOSE_MS = 90 * 1000;
const MAX_FILES = 10;
const STICKER_PREFIX = '__INTERNAL_CHAT_STICKER__';
const CHAT_STICKERS = [
    { id: 'ok', emoji: '👍', label: 'تمام', effect: 'animate-bounce' },
    { id: 'thanks', emoji: '🙏', label: 'شكرًا', effect: 'animate-pulse' },
    { id: 'happy', emoji: '😂', label: 'ضحك', effect: 'animate-bounce' },
    { id: 'clap', emoji: '👏', label: 'عاش', effect: 'animate-pulse' },
    { id: 'done', emoji: '✅', label: 'تم', effect: 'animate-bounce' },
    { id: 'fire', emoji: '🔥', label: 'جامد', effect: 'animate-pulse' },
    { id: 'idea', emoji: '💡', label: 'فكرة', effect: 'animate-bounce' },
    { id: 'warning', emoji: '⚠️', label: 'تنبيه', effect: 'animate-pulse' },
    { id: 'eyes', emoji: '👀', label: 'متابع', effect: 'animate-bounce' },
    { id: 'heart', emoji: '💙', label: 'تقدير', effect: 'animate-pulse' },
    { id: 'rocket', emoji: '🚀', label: 'انطلاق', effect: 'animate-bounce' },
    { id: 'party', emoji: '🎉', label: 'مبروك', effect: 'animate-pulse' }
];

const USER_ACCENT_COLORS = ['#075e54', '#0f766e', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#b45309', '#be123c'];

const REACTION_CHOICES = ['👍', '❤️', '😂', '😮', '😢', '🙏', '✅', '🔥'];

const CHAT_PASSPORT_PERMISSION_SECTION = 'الشات الداخلي';
const CHAT_PASSPORT_PERMISSION_ITEM = 'تسجيل الجواز من الشات الداخلي';

function normalizeChatUser(user = {}) {
    const id = user.id || user.UserId || user.userId || user.UserID || user.UserName || user.Username || user.username || user.email || user.Email;
    const name = user.name || user.FullName || user.fullName || user.DisplayName || user.displayName || user.Username || user.username || user.email || user.Email;
    const username = user.username || user.Username || user.UserName || user.email || user.Email || '';
    if (!id && !name) return null;
    return {
        id: String(id || name).trim(),
        name: String(name || id || 'مستخدم').trim(),
        username: String(username || '').trim()
    };
}

function mergeUniqueUsers(users = []) {
    const map = new Map();
    users.forEach(user => {
        const normalized = normalizeChatUser(user);
        if (!normalized?.id) return;
        const key = String(normalized.id);
        const existing = map.get(key) || {};
        map.set(key, {
            ...existing,
            ...normalized,
            name: normalized.name || existing.name || 'مستخدم',
            username: normalized.username || existing.username || ''
        });
    });
    return Array.from(map.values()).sort((a, b) => String(a.name).localeCompare(String(b.name), 'ar'));
}

function getUsersFromMessages(messageList = []) {
    const users = [];
    (Array.isArray(messageList) ? messageList : []).forEach(message => {
        if (message?.sender) users.push(message.sender);
        if (Array.isArray(message?.mentions)) users.push(...message.mentions);
        if (Array.isArray(message?.readBy)) users.push(...message.readBy);
    });
    return mergeUniqueUsers(users);
}

function getActiveMentionQuery(value = '') {
    const beforeCursor = String(value || '');
    const match = beforeCursor.match(/(^|\s)@([^@\n\r]*)$/u);
    if (!match) return { active: false, query: '' };
    const query = String(match[2] || '').trim();
    if (query.length > 40) return { active: false, query: '' };
    return { active: true, query };
}

function normalizeForSearch(value = '') {
    return String(value || '').toLowerCase().replace(/[\u064B-\u065F\u0670]/g, '').replace(/\s+/g, ' ').trim();
}

function replaceActiveMentionQuery(value = '', user = {}) {
    const name = String(user.name || user.username || '').trim();
    if (!name) return value;
    return String(value || '').replace(/(^|\s)@([^@\n\r]*)$/u, (full, prefix) => `${prefix}@${name} `);
}

function escapeRegExp(value = '') {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildMentionsFromText(text = '', selectedMentions = [], users = []) {
    const map = new Map();
    (Array.isArray(selectedMentions) ? selectedMentions : []).forEach(user => {
        const normalized = normalizeChatUser(user);
        if (normalized?.id) map.set(String(normalized.id), normalized);
    });

    const sourceText = String(text || '');
    mergeUniqueUsers(users).forEach(user => {
        const patterns = [user.name, user.username].filter(Boolean);
        const matched = patterns.some(pattern => {
            const escaped = escapeRegExp(pattern).replace(/\s+/g, '\\s+');
            const regex = new RegExp(`(^|\\s)@${escaped}(?=\\s|$|[،,.!؟])`, 'iu');
            return regex.test(sourceText);
        });
        if (matched) map.set(String(user.id), user);
    });

    return Array.from(map.values());
}

function normalizeReactionUser(user = {}) {
    const normalized = normalizeChatUser(user);
    return normalized || { id: String(user.id || user.userId || 'unknown'), name: String(user.name || user.username || 'مستخدم'), username: String(user.username || '') };
}

function normalizeReactions(raw = []) {
    if (!raw) return [];
    const groups = [];
    const addUser = (emoji, user) => {
        const value = String(emoji || '').trim();
        if (!value) return;
        const normalizedUser = normalizeReactionUser(user);
        const userId = String(normalizedUser.id || '').trim();
        if (!userId) return;
        let group = groups.find(item => item.emoji === value);
        if (!group) {
            group = { emoji: value, users: [] };
            groups.push(group);
        }
        if (!group.users.some(item => String(item.id) === userId)) {
            group.users.push(normalizedUser);
        }
    };

    if (Array.isArray(raw)) {
        raw.forEach(item => {
            if (typeof item === 'string') return;
            const emoji = item.emoji || item.reaction || item.value || item.type;
            if (Array.isArray(item.users)) {
                item.users.forEach(user => addUser(emoji, user));
            } else if (Array.isArray(item.reactedBy)) {
                item.reactedBy.forEach(user => addUser(emoji, user));
            } else if (item.user || item.userId || item.id) {
                addUser(emoji, item.user || item);
            }
        });
    } else if (typeof raw === 'object') {
        Object.entries(raw).forEach(([emoji, users]) => {
            if (Array.isArray(users)) users.forEach(user => addUser(emoji, user));
        });
    }

    return groups.filter(group => group.users.length > 0);
}

function toggleReactionGroups(groups = [], emoji = '', user = {}, action = 'toggle') {
    const value = String(emoji || '').trim();
    if (!value) return normalizeReactions(groups);
    const normalizedUser = normalizeReactionUser(user);
    const userId = String(normalizedUser.id || '').trim();
    if (!userId) return normalizeReactions(groups);

    const currentGroups = normalizeReactions(groups);
    const hadSame = currentGroups.some(group => group.emoji === value && group.users.some(item => String(item.id) === userId));
    const withoutUser = currentGroups
        .map(group => ({ ...group, users: group.users.filter(item => String(item.id) !== userId) }))
        .filter(group => group.users.length > 0);

    if (action === 'remove' || (action === 'toggle' && hadSame)) return withoutUser;

    let target = withoutUser.find(group => group.emoji === value);
    if (!target) {
        target = { emoji: value, users: [] };
        withoutUser.push(target);
    }
    target.users.push(normalizedUser);
    return withoutUser;
}

function mergeReactionGroups(serverGroups = [], localGroups = []) {
    const merged = normalizeReactions(serverGroups);
    normalizeReactions(localGroups).forEach(group => {
        group.users.forEach(user => {
            let target = merged.find(item => item.emoji === group.emoji);
            if (!target) {
                target = { emoji: group.emoji, users: [] };
                merged.push(target);
            }
            if (!target.users.some(item => String(item.id) === String(user.id))) {
                target.users.push(user);
            }
        });
    });
    return merged.filter(group => group.users.length > 0);
}

function getMyReactionEmoji(groups = [], myId = '') {
    const meId = String(myId || '');
    const group = normalizeReactions(groups).find(item => item.users.some(user => String(user.id) === meId));
    return group?.emoji || '';
}

function loadLocalChatReactions(userId = '') {
    try {
        const key = `internal_chat_reactions_${String(userId || 'unknown')}`;
        const parsed = JSON.parse(localStorage.getItem(key) || '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function saveLocalChatReactions(userId = '', value = {}) {
    try {
        const key = `internal_chat_reactions_${String(userId || 'unknown')}`;
        localStorage.setItem(key, JSON.stringify(value || {}));
    } catch (_) {
        // تجاهل أخطاء التخزين المحلي
    }
}

function getStableHash(value = '') {
    return String(value || '').split('').reduce((acc, char) => ((acc << 5) - acc) + char.charCodeAt(0), 0);
}

function getUserAccentColor(user = {}) {
    const key = String(user?.id || user?.userId || user?.username || user?.name || 'user');
    const index = Math.abs(getStableHash(key)) % USER_ACCENT_COLORS.length;
    return USER_ACCENT_COLORS[index];
}

function isMessageDeleted(message = {}, deletedMessageIds = new Set()) {
    const id = String(message?.id || '');
    return Boolean(
        (id && deletedMessageIds.has(id)) ||
        message?.deletedAt ||
        message?.isDeleted ||
        message?.deleted ||
        message?.status === 'deleted'
    );
}

function encodeStickerMessage(sticker) {
    return `${STICKER_PREFIX}${JSON.stringify({ id: sticker.id, emoji: sticker.emoji, label: sticker.label })}`;
}

function parseStickerMessage(text = '') {
    const value = String(text || '').trim();
    if (!value.startsWith(STICKER_PREFIX)) return null;

    try {
        const payload = JSON.parse(value.slice(STICKER_PREFIX.length));
        const preset = CHAT_STICKERS.find(item => item.id === payload.id);
        return {
            id: payload.id || preset?.id || 'custom',
            emoji: preset?.emoji || payload.emoji || '⭐',
            label: preset?.label || payload.label || 'ملصق',
            effect: preset?.effect || 'animate-bounce'
        };
    } catch (_) {
        return null;
    }
}

function getBaseURL() {
    return window.location.origin; // السيرفر على نفس الموقع
}

function getStoredUser() {
    try {
        return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
        return {};
    }
}

function normalizeUser(user = {}) {
    const stored = getStoredUser();
    const merged = { ...stored, ...user };
    const id = merged.UserId || merged.userId || merged.id || merged.Username || merged.username || 'unknown';
    const name = merged.FullName || merged.fullName || merged.name || merged.Username || merged.username || 'مستخدم';
    const username = merged.Username || merged.username || '';
    return { id: String(id), name: String(name), username: String(username) };
}

function formatTime(value) {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString('ar-EG', {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: '2-digit'
        });
    } catch {
        return '';
    }
}

function formatSize(bytes = 0) {
    const size = Number(bytes || 0);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function isImageAttachment(att) {
    return String(att?.mimeType || '').startsWith('image/');
}

function normalizeReadBy(readBy = []) {
    if (!Array.isArray(readBy)) return [];
    const unique = new Map();
    readBy.forEach(item => {
        const id = String(item?.id || item?.userId || '').trim();
        if (!id || unique.has(id)) return;
        unique.set(id, {
            id,
            name: String(item?.name || item?.fullName || item?.username || 'مستخدم'),
            username: String(item?.username || ''),
            readAt: item?.readAt || ''
        });
    });
    return Array.from(unique.values());
}

function getSeenBy(message, myId) {
    const senderId = String(message?.sender?.id || '');
    const meId = String(myId || '');
    return normalizeReadBy(message?.readBy || [])
        .filter(item => String(item.id) !== senderId && String(item.id) !== meId);
}

function getMessagePreviewText(message = {}, deletedMessageIds = new Set()) {
    if (isMessageDeleted(message, deletedMessageIds)) return 'تم حذف هذه الرسالة';
    const sticker = parseStickerMessage(message?.text || '');
    if (sticker) return `${sticker.emoji} ${sticker.label}`;
    const text = String(message?.text || '').trim();
    if (text) return text.length > 95 ? `${text.slice(0, 95)}...` : text;
    const attachmentsCount = Array.isArray(message?.attachments) ? message.attachments.length : 0;
    if (attachmentsCount > 0) return attachmentsCount === 1 ? 'مرفق' : `${attachmentsCount} مرفقات`;
    return 'رسالة';
}

function buildReplyPreview(message = {}, deletedMessageIds = new Set()) {
    if (!message?.id) return null;
    const sender = normalizeChatUser(message.sender || {}) || { id: '', name: 'مستخدم', username: '' };
    return {
        id: String(message.id),
        sender,
        text: getMessagePreviewText(message, deletedMessageIds),
        createdAt: message.createdAt || '',
        deleted: isMessageDeleted(message, deletedMessageIds)
    };
}

function normalizeReplyPreview(replyTo = {}) {
    if (!replyTo || typeof replyTo !== 'object') return null;
    const id = String(replyTo.id || replyTo.messageId || '').trim();
    if (!id) return null;
    const sender = normalizeChatUser(replyTo.sender || replyTo.user || {}) || {
        id: String(replyTo.senderId || '').trim(),
        name: String(replyTo.senderName || replyTo.name || 'مستخدم'),
        username: String(replyTo.senderUsername || replyTo.username || '')
    };
    const text = String(replyTo.text || replyTo.preview || replyTo.messageText || 'رسالة').trim() || 'رسالة';
    return {
        id,
        sender,
        text: text.length > 120 ? `${text.slice(0, 120)}...` : text,
        createdAt: replyTo.createdAt || '',
        deleted: Boolean(replyTo.deleted || replyTo.isDeleted || replyTo.deletedAt)
    };
}

function isUnreadForUser(message, myId) {
    if (!message?.id) return false;
    const meId = String(myId || '');
    if (!meId || String(message.sender?.id || '') === meId) return false;
    return !normalizeReadBy(message.readBy || []).some(item => String(item.id) === meId);
}

function getFirstUnreadId(messageList = [], myId) {
    const firstUnread = (Array.isArray(messageList) ? messageList : []).find(message => isUnreadForUser(message, myId));
    return firstUnread?.id ? String(firstUnread.id) : null;
}

function countUnreadMessages(messageList = [], myId) {
    return (Array.isArray(messageList) ? messageList : []).filter(message => isUnreadForUser(message, myId)).length;
}

function isMentionedUser(message, myId) {
    const meId = String(myId || '');
    if (!meId || message?.isDeleted || message?.deletedAt) return false;
    return (Array.isArray(message?.mentions) ? message.mentions : [])
        .some(item => String(item?.id || item?.userId || '').trim() === meId);
}

function isUnreadMentionForUser(message, myId) {
    return isUnreadForUser(message, myId) && isMentionedUser(message, myId);
}

function getFirstUnreadMentionId(messageList = [], myId) {
    const firstMention = (Array.isArray(messageList) ? messageList : []).find(message => isUnreadMentionForUser(message, myId));
    return firstMention?.id ? String(firstMention.id) : null;
}

function countUnreadMentions(messageList = [], myId) {
    return (Array.isArray(messageList) ? messageList : []).filter(message => isUnreadMentionForUser(message, myId)).length;
}

function mergeMessages(current, incoming) {
    const map = new Map();
    [...current, ...incoming].forEach(message => {
        if (!message?.id) return;
        const existing = map.get(message.id);
        if (existing) {
            map.set(message.id, {
                ...existing,
                ...message,
                readBy: normalizeReadBy([...(existing.readBy || []), ...(message.readBy || [])])
            });
        } else {
            map.set(message.id, {
                ...message,
                readBy: normalizeReadBy(message.readBy || [])
            });
        }
    });
    return Array.from(map.values()).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
}

function playNotificationSound(type = 'normal') {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const volume = type === 'mention' ? 0.42 : 0.32; // أعلى من النسخة السابقة
        const tones = type === 'mention' ? [980, 1320, 980] : [880, 1120];

        tones.forEach((frequency, index) => {
            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();
            const start = ctx.currentTime + index * 0.14;
            const end = start + 0.12;

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(frequency, start);
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(volume, start + 0.025);
            gain.gain.exponentialRampToValueAtTime(0.0001, end);
            oscillator.connect(gain);
            gain.connect(ctx.destination);
            oscillator.start(start);
            oscillator.stop(end + 0.02);
        });

        setTimeout(() => ctx.close().catch(() => {}), 900);
    } catch (_) {
        // بعض المتصفحات تمنع الصوت قبل أول تفاعل من المستخدم
    }
}

function readPermissionObject(value) {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        const parsed = JSON.parse(String(value));
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

export default function InternalChat({ isLoggedIn, currentUser, userPermissions = {} }) {
    const baseURL = useMemo(getBaseURL, []);
    const me = useMemo(() => normalizeUser(currentUser || {}), [currentUser]);
    const canRegisterPassportFromChat = useMemo(() => {
        const rawId = currentUser?.UserId ?? currentUser?.userId ?? currentUser?.id ?? me.id;
        if (String(rawId) === '-1') return true;

        const explicitPermissions = readPermissionObject(userPermissions);
        if (explicitPermissions?.[CHAT_PASSPORT_PERMISSION_SECTION]?.[CHAT_PASSPORT_PERMISSION_ITEM] === true) {
            return true;
        }

        const userStoredPermissions = readPermissionObject(currentUser?.Permissions);
        return userStoredPermissions?.[CHAT_PASSPORT_PERMISSION_SECTION]?.[CHAT_PASSPORT_PERMISSION_ITEM] === true;
    }, [currentUser, me.id, userPermissions]);

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [input, setInput] = useState('');
    const [files, setFiles] = useState([]);
    const [mentions, setMentions] = useState([]);
    const [unread, setUnread] = useState(0);
    const [mentionUnread, setMentionUnread] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const [unreadAnchorId, setUnreadAnchorId] = useState(null);
    const [showStickerPanel, setShowStickerPanel] = useState(false);
    const [showOnlinePanel, setShowOnlinePanel] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [mentionSearch, setMentionSearch] = useState({ active: false, query: '' });
    const [reactionTargetId, setReactionTargetId] = useState(null);
    const [localReactions, setLocalReactions] = useState(() => loadLocalChatReactions(me.id));
    const [deletedMessageIds, setDeletedMessageIds] = useState(() => new Set());
    const [deletingMessageIds, setDeletingMessageIds] = useState(() => new Set());
    const [deleteConfirmMessage, setDeleteConfirmMessage] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [seenDetailsMessage, setSeenDetailsMessage] = useState(null);
    const [registeringAttachmentKeys, setRegisteringAttachmentKeys] = useState(() => new Set());

    const socketRef = useRef(null);
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);
    const idleTimerRef = useRef(null);
    const typingTimerRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const textAreaRef = useRef(null);
    const reactionServerWarningShownRef = useRef(false);
    const isOpenRef = useRef(false);
    const messagesRef = useRef([]);

    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const filteredOnlineUsers = useMemo(() => {
        return onlineUsers.filter(user => String(user.id) !== String(me.id));
    }, [onlineUsers, me.id]);

    const selectedMentionIds = useMemo(() => new Set(mentions.map(u => String(u.id))), [mentions]);

    const knownMentionUsers = useMemo(() => {
        const usersFromMessages = getUsersFromMessages(messages);
        return mergeUniqueUsers([...allUsers, ...onlineUsers, ...usersFromMessages])
            .filter(user => String(user.id) !== String(me.id));
    }, [allUsers, onlineUsers, messages, me.id]);

    const mentionSuggestions = useMemo(() => {
        if (!mentionSearch.active) return [];
        const query = normalizeForSearch(mentionSearch.query);
        const source = knownMentionUsers;
        const results = query
            ? source.filter(user => {
                const haystack = normalizeForSearch(`${user.name || ''} ${user.username || ''}`);
                return haystack.includes(query);
            })
            : source;
        return results.slice(0, 8);
    }, [knownMentionUsers, mentionSearch.active, mentionSearch.query]);

    const reactionTargetMessage = useMemo(() => {
        if (!reactionTargetId) return null;
        return messages.find(message => String(message.id) === String(reactionTargetId)) || null;
    }, [messages, reactionTargetId]);

    const seenDetailsLiveMessage = useMemo(() => {
        if (!seenDetailsMessage?.id) return null;
        return messages.find(message => String(message.id) === String(seenDetailsMessage.id)) || seenDetailsMessage;
    }, [messages, seenDetailsMessage]);

    const resetIdleTimer = () => {
        if (!isOpen) return;
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
            setIsOpen(false);
        }, AUTO_CLOSE_MS);
    };

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior, block: 'end' }), 80);
    }, []);

    const scrollToMessage = useCallback((messageId, behavior = 'smooth') => {
        if (!messageId) return false;
        setTimeout(() => {
            const target = document.getElementById(`internal-chat-message-${messageId}`);
            if (target) {
                target.scrollIntoView({ behavior, block: 'center' });
            }
        }, 120);
        return true;
    }, []);

    const scrollToUnreadOrBottom = useCallback((messageList = messagesRef.current, behavior = 'smooth') => {
        const firstUnreadId = getFirstUnreadId(messageList, me.id);
        setUnreadAnchorId(firstUnreadId);
        if (firstUnreadId) {
            scrollToMessage(firstUnreadId, behavior);
            return firstUnreadId;
        }
        scrollToBottom(behavior);
        return null;
    }, [me.id, scrollToBottom, scrollToMessage]);

    const scrollToMentionOrUnreadOrBottom = useCallback((messageList = messagesRef.current, behavior = 'smooth') => {
        const firstMentionId = getFirstUnreadMentionId(messageList, me.id);
        const firstTargetId = firstMentionId || getFirstUnreadId(messageList, me.id);
        setUnreadAnchorId(firstTargetId);
        if (firstTargetId) {
            scrollToMessage(firstTargetId, behavior);
            return firstTargetId;
        }
        scrollToBottom(behavior);
        return null;
    }, [me.id, scrollToBottom, scrollToMessage]);

    const applyReadReceiptUpdates = useCallback((updates = []) => {
        if (!Array.isArray(updates) || updates.length === 0) return;
        const updatesMap = new Map(updates.map(item => [String(item.id), normalizeReadBy(item.readBy || [])]));
        setMessages(prev => prev.map(message => {
            const nextReadBy = updatesMap.get(String(message.id));
            if (!nextReadBy) return message;
            return {
                ...message,
                readBy: normalizeReadBy([...(message.readBy || []), ...nextReadBy])
            };
        }));
    }, []);


    const applyMessageDeleted = useCallback((payload = {}) => {
        const rawIds = payload.messageIds || payload.ids || [payload.messageId || payload.id];
        const ids = rawIds.map(id => String(id || '')).filter(Boolean);
        if (ids.length === 0) return;

        const deletedAt = payload.deletedAt || new Date().toISOString();
        const deletedBy = payload.deletedBy || payload.user || { id: me.id, name: me.name, username: me.username };

        setDeletedMessageIds(prev => {
            const next = new Set(prev);
            ids.forEach(id => next.add(id));
            return next;
        });

        setMessages(prev => prev.map(message => {
            if (!ids.includes(String(message.id))) return message;
            return {
                ...message,
                text: '',
                attachments: [],
                mentions: [],
                reactions: [],
                deletedAt,
                deletedBy,
                isDeleted: true
            };
        }));
    }, [me.id, me.name, me.username]);

    const applyMessageUpdated = useCallback((updatedMessage = {}) => {
        if (!updatedMessage?.id) return;
        setMessages(prev => mergeMessages(prev, [updatedMessage]));
    }, []);

    const getMessageReactions = useCallback((message = {}) => {
        const messageId = String(message?.id || '');
        const serverReactions = message?.reactions || message?.Reactions || message?.reactionGroups || [];
        const savedLocalReactions = messageId ? (localReactions[messageId] || []) : [];
        return mergeReactionGroups(serverReactions, savedLocalReactions);
    }, [localReactions]);

    const applyReactionUpdate = useCallback((payload = {}) => {
        const messageId = String(payload.messageId || payload.id || payload.message?.id || '');
        if (!messageId) return;

        const hasFullGroups = Array.isArray(payload.reactions) || payload.reactionGroups || payload.reactionsGroups;
        const fullGroups = payload.reactions || payload.reactionGroups || payload.reactionsGroups;
        const emoji = payload.emoji || payload.reaction || payload.value || payload.type;
        const user = payload.user || payload.sender || payload.reactedBy || payload.by || payload;
        const action = payload.action || payload.mode || 'toggle';

        setMessages(prev => prev.map(message => {
            if (String(message.id) !== messageId) return message;
            const current = message.reactions || [];
            const nextReactions = hasFullGroups
                ? normalizeReactions(fullGroups)
                : toggleReactionGroups(current, emoji, user, action);
            return { ...message, reactions: nextReactions };
        }));
    }, []);

    const toggleMessageReaction = async (message, emoji) => {
        const messageId = String(message?.id || '');
        if (!messageId || !emoji) return;

        const user = { id: me.id, name: me.name, username: me.username };
        const currentGroups = getMessageReactions(message);
        const nextGroups = toggleReactionGroups(currentGroups, emoji, user, 'toggle');

        setLocalReactions(prev => {
            const next = { ...prev, [messageId]: nextGroups };
            saveLocalChatReactions(me.id, next);
            return next;
        });

        setMessages(prev => prev.map(item => {
            if (String(item.id) !== messageId) return item;
            return { ...item, reactions: nextGroups };
        }));

        setReactionTargetId(null);
        resetIdleTimer();

        const payload = {
            messageId,
            emoji,
            reaction: emoji,
            senderId: me.id,
            senderName: me.name,
            senderUsername: me.username,
            user,
            reactions: nextGroups
        };

        socketRef.current?.emit('chat:reaction', payload);
        socketRef.current?.emit('chat:message-reaction', payload);

        const headers = {
            'Content-Type': 'application/json',
            'X-User-Id': me.id,
            'X-User-Name': encodeURIComponent(me.name),
            'X-Username': me.username
        };

        const endpoints = [
            `${baseURL}/api/chat/messages/${encodeURIComponent(messageId)}/reactions`,
            `${baseURL}/api/chat/messages/${encodeURIComponent(messageId)}/reaction`,
            `${baseURL}/api/chat/reactions`
        ];

        let savedOnServer = false;
        for (const endpoint of endpoints) {
            try {
                const res = await fetch(endpoint, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(payload)
                });
                if (!res.ok) continue;
                const data = await res.json().catch(() => ({}));
                if (data.success === false) continue;
                savedOnServer = true;
                if (data.reactions || data.reactionGroups || data.message) {
                    applyReactionUpdate({
                        messageId,
                        reactions: data.reactions || data.reactionGroups || data.message?.reactions || nextGroups
                    });
                }
                break;
            } catch (_) {
                // جرّب المسار التالي
            }
        }

        if (!savedOnServer && !reactionServerWarningShownRef.current) {
            reactionServerWarningShownRef.current = true;
            toast('تم عرض التفاعل عندك. لو عايزه يظهر لكل المستخدمين ويثبت بعد الريفريش، استبدل ملف سيرفر الشات بالنسخة المرفقة.', { icon: 'ℹ️', duration: 6500 });
        }
    };

    const registerPassportFromAttachment = async (message, attachment) => {
        if (!isLoggedIn || !message?.id || !attachment?.id) return;
        if (!canRegisterPassportFromChat) {
            toast.error('لا توجد صلاحية لتسجيل الجواز من الشات الداخلي.');
            return;
        }
        const key = `${message.id}:${attachment.id}`;
        const currentRegistration = attachment.passportRegistration || {};
        if (currentRegistration.status === 'processing' || currentRegistration.status === 'success' || currentRegistration.status === 'duplicate') return;

        setRegisteringAttachmentKeys(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });

        setMessages(prev => prev.map(item => {
            if (String(item.id) !== String(message.id)) return item;
            return {
                ...item,
                attachments: (item.attachments || []).map(att => (
                    String(att.id) === String(attachment.id)
                        ? { ...att, passportRegistration: { status: 'processing', startedAt: new Date().toISOString(), startedBy: me } }
                        : att
                ))
            };
        }));

        try {
            const response = await fetch(`${baseURL}/api/chat/messages/${encodeURIComponent(message.id)}/attachments/${encodeURIComponent(attachment.id)}/register-passport`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-User-Id': me.id,
                    'X-User-Name': encodeURIComponent(me.name),
                    'X-Username': me.username
                },
                body: JSON.stringify({
                    senderId: me.id,
                    senderName: me.name,
                    senderUsername: me.username
                })
            });
            const data = await response.json().catch(() => ({}));
            if (data.message?.id) applyMessageUpdated(data.message);
            if (!response.ok || data.success === false) {
                throw new Error(data.error || 'تعذر تسجيل الجواز تلقائيًا');
            }

            if (data.duplicate) {
                toast('الجواز مسجل مسبقًا في النظام.', { icon: '⚠️', duration: 5000 });
            } else {
                toast.success(`تم تسجيل الجواز بنجاح${data.registration?.officeSerial ? ` - رقم الملف ${data.registration.officeSerial}` : ''}`);
            }
        } catch (error) {
            toast.error(error.message || 'تعذر تسجيل الجواز تلقائيًا. يرجى المحاولة مرة أخرى.');
            await fetchMessages({ markRead: false });
        } finally {
            setRegisteringAttachmentKeys(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const markMessagesRead = useCallback(async (messageList = messagesRef.current) => {
        const readAt = new Date().toISOString();
        const idsToMark = (Array.isArray(messageList) ? messageList : [])
            .filter(message => message?.id)
            .filter(message => String(message.sender?.id) !== String(me.id))
            .filter(message => !normalizeReadBy(message.readBy || []).some(item => String(item.id) === String(me.id)))
            .map(message => String(message.id));

        if (idsToMark.length === 0) return;

        setMessages(prev => prev.map(message => {
            if (!idsToMark.includes(String(message.id))) return message;
            return {
                ...message,
                readBy: normalizeReadBy([
                    ...(message.readBy || []),
                    { id: me.id, name: me.name, username: me.username, readAt }
                ])
            };
        }));

        try {
            const res = await fetch(`${baseURL}/api/chat/read`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-User-Id': me.id,
                    'X-User-Name': encodeURIComponent(me.name),
                    'X-Username': me.username
                },
                body: JSON.stringify({
                    senderId: me.id,
                    senderName: me.name,
                    senderUsername: me.username,
                    messageIds: idsToMark
                })
            });
            const data = await res.json().catch(() => ({}));
            if (data.success && Array.isArray(data.updated)) {
                applyReadReceiptUpdates(data.updated);
            }
        } catch (err) {
            // fallback سريع لو الشبكة قطعت لحظة القراءة
            socketRef.current?.emit('chat:read', { messageIds: idsToMark });
        }
    }, [applyReadReceiptUpdates, baseURL, me.id, me.name, me.username]);

    const fetchMessages = useCallback(async ({ markRead = false, scrollMode = 'none' } = {}) => {
        try {
            const res = await fetch(`${baseURL}/api/chat/messages?limit=180`, {
                headers: {
                    'X-User-Id': me.id,
                    'X-User-Name': encodeURIComponent(me.name),
                    'X-Username': me.username
                }
            });
            const data = await res.json();
            if (data.success) {
                const loadedMessages = data.messages || [];
                setMessages(loadedMessages);
                setOnlineUsers(data.onlineUsers || []);

                const serverUnreadCount = countUnreadMessages(loadedMessages, me.id);
                const serverMentionUnreadCount = countUnreadMentions(loadedMessages, me.id);
                if (markRead) {
                    setUnread(0);
                    setMentionUnread(0);
                } else if (!isOpenRef.current) {
                    // مهم بعد الـ Refresh: الرسائل غير المقروءة كانت موجودة على السيرفر
                    // لكنها لن تأتي كـ socket event جديد، لذلك نحسبها من readBy ونظهرها على الزر.
                    setUnread(serverUnreadCount);
                    setMentionUnread(serverMentionUnreadCount);
                }

                if (scrollMode === 'mention-or-unread') {
                    scrollToMentionOrUnreadOrBottom(loadedMessages, 'auto');
                } else if (scrollMode === 'unread') {
                    scrollToUnreadOrBottom(loadedMessages, 'auto');
                } else if (scrollMode === 'bottom') {
                    setUnreadAnchorId(null);
                    scrollToBottom('auto');
                }

                if (markRead) {
                    markMessagesRead(loadedMessages);
                }

                return loadedMessages;
            }
        } catch (err) {
            console.error('فشل تحميل الشات:', err);
        }
        return [];
    }, [baseURL, me.id, me.name, me.username, markMessagesRead, scrollToBottom, scrollToMentionOrUnreadOrBottom, scrollToUnreadOrBottom]);

    useEffect(() => {
        if (!isLoggedIn || !me.id || me.id === 'unknown') return undefined;

        fetchMessages({ markRead: false });
        const socket = io(baseURL, {
            transports: ['websocket', 'polling'],
            auth: {
                userId: me.id,
                name: me.name,
                username: me.username
            }
        });
        socketRef.current = socket;

        socket.on('connect_error', (err) => {
            console.error('خطأ اتصال الشات:', err.message);
        });

        socket.on('chat:presence', (users) => {
            setOnlineUsers(Array.isArray(users) ? users : []);
        });

        socket.on('chat:typing', (payload) => {
            if (!payload?.user || String(payload.user.id) === String(me.id)) return;
            setTypingUser(payload.user);
            clearTimeout(typingTimerRef.current);
            typingTimerRef.current = setTimeout(() => setTypingUser(null), 2500);
        });

        socket.on('chat:message', (message) => {
            if (!message?.id) return;
            const isMine = String(message.sender?.id) === String(me.id);
            const mentionedMe = (message.mentions || []).some(item => String(item.id) === String(me.id));
            const readableNow = isOpenRef.current && !document.hidden;

            setMessages(prev => mergeMessages(prev, [message]));
            if (isMine || readableNow) {
                setUnreadAnchorId(null);
                scrollToBottom();
            }

            if (!isMine && readableNow) {
                setUnread(0);
                setMentionUnread(0);
                markMessagesRead([message]);
                return;
            }

            if (!isMine) {
                setUnread(prev => prev + 1);
                if (mentionedMe) {
                    setMentionUnread(prev => prev + 1);
                } else {
                    playNotificationSound('normal');
                    toast(`رسالة جديدة من ${message.sender?.name || 'مستخدم'}`, { icon: '💬' });
                }
            }
        });

        socket.on('chat:mention', (payload = {}) => {
            if (String(payload.sender?.id) === String(me.id)) return;
            playNotificationSound('mention');
            toast(`📣 ${payload.sender?.name || 'مستخدم'} عمل لك إشارة في الشات`, {
                duration: 6500,
                icon: '📣'
            });
        });

        socket.on('chat:read-receipts', (payload = {}) => {
            applyReadReceiptUpdates(payload.messages || []);
        });


        socket.on('chat:message-updated', (message = {}) => {
            applyMessageUpdated(message);
        });

        socket.on('chat:message-deleted', (payload = {}) => {
            applyMessageDeleted(payload);
        });

        socket.on('chat:delete-message', (payload = {}) => {
            applyMessageDeleted(payload);
        });

        socket.on('chat:reaction-updated', (payload = {}) => {
            applyReactionUpdate(payload);
        });

        socket.on('chat:reaction', (payload = {}) => {
            applyReactionUpdate(payload);
        });

        socket.on('chat:message-reaction', (payload = {}) => {
            applyReactionUpdate(payload);
        });

        return () => {
            clearTimeout(typingTimerRef.current);
            socket.disconnect();
        };
    }, [isLoggedIn, me.id, me.name, me.username, baseURL, fetchMessages, markMessagesRead, applyReadReceiptUpdates, applyMessageDeleted, applyMessageUpdated, applyReactionUpdate]);

    useEffect(() => {
        if (isOpen) {
            resetIdleTimer();
        } else {
            setUnreadAnchorId(null);
        }
        return () => clearTimeout(idleTimerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        const handleBackToChat = () => {
            if (isOpenRef.current && !document.hidden) {
                setUnread(0);
                setMentionUnread(0);
                markMessagesRead(messagesRef.current);
            }
        };

        document.addEventListener('visibilitychange', handleBackToChat);
        window.addEventListener('focus', handleBackToChat);
        return () => {
            document.removeEventListener('visibilitychange', handleBackToChat);
            window.removeEventListener('focus', handleBackToChat);
        };
    }, [markMessagesRead]);

    if (!isLoggedIn) return null;

    const addFiles = (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        setFiles(prev => [...prev, ...incoming].slice(0, MAX_FILES));
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const addMention = (user) => {
        if (!user) return;
        setMentions(prev => {
            if (prev.some(item => String(item.id) === String(user.id))) return prev;
            return [...prev, { id: user.id, name: user.name }];
        });
        setInput(prev => {
            const mentionText = `@${user.name}`;
            return prev.includes(mentionText) ? prev : `${prev}${prev.endsWith(' ') || !prev ? '' : ' '}${mentionText} `;
        });
        resetIdleTimer();
    };

    const removeMention = (userId) => {
        setMentions(prev => prev.filter(item => String(item.id) !== String(userId)));
    };

    const handlePaste = (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageFiles = items
            .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
            .map(item => item.getAsFile())
            .filter(Boolean)
            .map((file, index) => new File([file], `pasted-image-${Date.now()}-${index}.png`, { type: file.type || 'image/png' }));

        if (imageFiles.length) {
            e.preventDefault();
            addFiles(imageFiles);
            toast.success('تم إضافة الصورة المنسوخة للمرفقات');
        }
        resetIdleTimer();
    };

    const startReplyToMessage = (message) => {
        const preview = buildReplyPreview(message, deletedMessageIds);
        if (!preview) return;
        setReplyTo(preview);
        setReactionTargetId(null);
        setShowStickerPanel(false);
        setTimeout(() => textAreaRef.current?.focus(), 40);
        resetIdleTimer();
    };

    const openSeenDetails = (message) => {
        if (!message?.id) return;
        setSeenDetailsMessage(message);
        resetIdleTimer();
    };

    const sendMessage = async (options = {}) => {
        const forcedText = typeof options.text === 'string' ? options.text : null;
        const text = (forcedText ?? input).trim();
        const filesToSend = forcedText ? [] : files;
        const mentionsToSend = forcedText ? [] : mentions;
        const replyToSend = replyTo ? normalizeReplyPreview(replyTo) : null;
        if (!text && filesToSend.length === 0) return;
        if (isSending) return;

        setIsSending(true);
        try {
            const form = new FormData();
            form.append('text', text);
            form.append('senderId', me.id);
            form.append('senderName', me.name);
            form.append('senderUsername', me.username);
            form.append('mentions', JSON.stringify(mentionsToSend));
            form.append('replyTo', JSON.stringify(replyToSend));
            filesToSend.forEach(file => form.append('attachments', file));

            const res = await fetch(`${baseURL}/api/chat/messages`, {
                method: 'POST',
                headers: {
                    'X-User-Id': me.id,
                    'X-User-Name': encodeURIComponent(me.name),
                    'X-Username': me.username
                },
                body: form
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || 'فشل إرسال الرسالة');
            }
            if (!forcedText) {
                setInput('');
                setFiles([]);
                setMentions([]);
            }
            setReplyTo(null);
            setShowStickerPanel(false);
            scrollToBottom();
        } catch (err) {
            toast.error(err.message || 'فشل إرسال الرسالة');
        } finally {
            setIsSending(false);
            resetIdleTimer();
        }
    };

    const sendSticker = (sticker) => {
        if (!sticker || isSending) return;
        sendMessage({ text: encodeStickerMessage(sticker) });
    };


    const deleteMessageForEveryone = (message) => {
        const messageId = String(message?.id || '');
        if (!messageId) return;
        if (String(message.sender?.id || '') !== String(me.id)) {
            toast.error('يمكنك حذف رسائلك أنت فقط لدى الجميع');
            return;
        }
        setReactionTargetId(null);
        setDeleteConfirmMessage(message);
        resetIdleTimer();
    };

    const confirmDeleteMessageForEveryone = async () => {
        const message = deleteConfirmMessage;
        const messageId = String(message?.id || '');
        if (!messageId) {
            setDeleteConfirmMessage(null);
            return;
        }

        setDeleteConfirmMessage(null);
        setDeletingMessageIds(prev => {
            const next = new Set(prev);
            next.add(messageId);
            return next;
        });

        const headers = {
            'Content-Type': 'application/json',
            'X-User-Id': me.id,
            'X-User-Name': encodeURIComponent(me.name),
            'X-Username': me.username
        };
        const body = JSON.stringify({
            senderId: me.id,
            senderName: me.name,
            senderUsername: me.username,
            messageId
        });

        try {
            let res = await fetch(`${baseURL}/api/chat/messages/${encodeURIComponent(messageId)}`, {
                method: 'DELETE',
                headers,
                body
            });

            if (res.status === 404 || res.status === 405) {
                res = await fetch(`${baseURL}/api/chat/messages/${encodeURIComponent(messageId)}/delete`, {
                    method: 'POST',
                    headers,
                    body
                });
            }

            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) {
                throw new Error(data.error || 'تعذر حذف الرسالة من السيرفر');
            }

            applyMessageDeleted({
                messageId,
                deletedAt: data.deletedAt,
                deletedBy: { id: me.id, name: me.name, username: me.username }
            });
            socketRef.current?.emit('chat:message-deleted', { messageId, deletedBy: { id: me.id, name: me.name, username: me.username } });
            toast.success('تم حذف الرسالة لدى الجميع');
        } catch (err) {
            const messageText = String(err?.message || 'تعذر حذف الرسالة. تأكد من تشغيل سيرفر الشات.');
            toast.error(messageText.includes('http://') || messageText.includes('https://') || messageText.includes(':5000') ? 'تعذر حذف الرسالة. تأكد من تشغيل سيرفر الشات.' : messageText);
        } finally {
            setDeletingMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageId);
                return next;
            });
            resetIdleTimer();
        }
    };

    const handleTyping = (value) => {
        setInput(value);
        if (showStickerPanel) setShowStickerPanel(false);
        resetIdleTimer();
        socketRef.current?.emit('chat:typing', { text: value.slice(0, 60) });
    };

    const openChat = async () => {
        setIsOpen(true);
        setUnread(0);
        setMentionUnread(0);

        const cachedMessages = messagesRef.current || [];
        if (cachedMessages.length > 0) {
            const firstTargetId = mentionUnread > 0
                ? scrollToMentionOrUnreadOrBottom(cachedMessages, 'auto')
                : scrollToUnreadOrBottom(cachedMessages, 'auto');
            if (firstTargetId) {
                setTimeout(() => markMessagesRead(cachedMessages), 450);
            }
            return;
        }

        const loadedMessages = await fetchMessages({ markRead: false, scrollMode: 'mention-or-unread' });
        const firstTargetAfterLoad = getFirstUnreadMentionId(loadedMessages, me.id) || getFirstUnreadId(loadedMessages, me.id);
        setUnread(0);
        setMentionUnread(0);
        if (firstTargetAfterLoad) {
            setTimeout(() => markMessagesRead(loadedMessages), 450);
        }
    };

    return (
        <div
            dir="rtl"
            className={`fixed z-[10050] font-sans ${isOpen
                ? 'right-2 left-2 bottom-[calc(72px+env(safe-area-inset-bottom))] sm:right-4 sm:left-auto md:right-auto md:left-4 md:bottom-6'
                : 'right-3 bottom-[calc(76px+env(safe-area-inset-bottom))] md:right-auto md:left-4 md:bottom-6'
            }`}
        >
            {!isOpen && (
                <button
                    type="button"
                    onClick={openChat}
                    className="relative group w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#00a884] text-white shadow-[0_10px_30px_rgba(0,168,132,0.35)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform border border-white/30"
                    title="الشات الداخلي"
                >
                    <MessageCircle className="w-6 h-6 md:w-7 md:h-7" />
                    {unread > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 md:min-w-6 md:h-6 px-1.5 rounded-full bg-red-600 text-white text-[10px] md:text-xs font-black flex items-center justify-center border-2 border-white">
                            {unread > 99 ? '99+' : unread}
                        </span>
                    )}
                    {mentionUnread > 0 && (
                        <span
                            className="absolute -top-1.5 -left-1.5 min-w-6 h-6 px-1.5 rounded-full bg-amber-400 text-amber-950 text-[10px] md:text-xs font-black flex items-center justify-center gap-0.5 border-2 border-white shadow-lg animate-pulse"
                            title="لديك إشارة في الشات"
                        >
                            <AtSign className="w-3 h-3" />
                            {mentionUnread > 1 && <span>{mentionUnread > 9 ? '9+' : mentionUnread}</span>}
                        </span>
                    )}
                    <span className="hidden md:block absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-slate-900 text-white text-xs rounded-xl px-3 py-2 shadow-lg">
                        شات السيستم الداخلي
                    </span>
                </button>
            )}

            {isOpen && (
                <div
                    className={`w-full sm:w-[392px] md:w-[455px] max-w-[calc(100vw-16px)] sm:max-w-[392px] md:max-w-[455px] ml-auto md:ml-0 h-[calc(100vh-112px)] max-h-[570px] sm:h-[610px] sm:max-h-[630px] md:h-[675px] md:max-h-[calc(100vh-48px)] bg-[#f0f2f5] dark:bg-[#1f2c34] rounded-[24px] md:rounded-[28px] shadow-[0_18px_55px_rgba(15,23,42,0.25)] border border-black/10 overflow-hidden flex flex-col ${isDragging ? 'ring-4 ring-[#00a884]/30' : ''}`}
                    onMouseMove={resetIdleTimer}
                    onKeyDown={resetIdleTimer}
                    onClick={resetIdleTimer}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        addFiles(e.dataTransfer.files);
                    }}
                >
                    <div className="bg-[#075e54] text-white px-3 py-2.5 md:px-4 md:py-3 shadow-sm relative z-10">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0 ring-1 ring-white/15">
                                    <MessageCircle className="w-5 h-5 md:w-5 md:h-5" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-black text-[15px] md:text-base leading-tight">الشات الداخلي</h3>
                                    <p className="text-[11px] text-emerald-50/90 truncate">
                                        {filteredOnlineUsers.length + 1} متصل الآن
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => setShowOnlinePanel(prev => !prev)}
                                    className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-colors ${showOnlinePanel ? 'bg-white text-[#075e54] dark:text-[#25d366]' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                                    title="المتصلون"
                                >
                                    <Users className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                                    title="تصغير"
                                >
                                    <Minus className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setIsOpen(false); setUnread(0); }}
                                    className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                                    title="إغلاق"
                                >
                                    <X className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                            </div>
                        </div>

                        {showOnlinePanel && (
                            <div className="mt-2 rounded-2xl bg-white/10 p-2 border border-white/10">
                                <div className="flex gap-2 overflow-x-auto pb-0.5">
                                    <span className="shrink-0 px-3 py-1.5 rounded-full bg-white text-[#075e54] dark:text-[#25d366] text-xs font-black">
                                        أنت: {me.name}
                                    </span>
                                    {filteredOnlineUsers.length === 0 ? (
                                        <span className="text-xs text-white/70 px-2 py-1.5">لا يوجد مستخدمون آخرون حالياً</span>
                                    ) : filteredOnlineUsers.map(user => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => addMention(user)}
                                            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-black border transition-colors ${selectedMentionIds.has(String(user.id)) ? 'bg-[#25d366] text-[#073b34] dark:text-[#d1f4cc] border-[#25d366]' : 'bg-white/10 text-white border-white/15 hover:bg-white/20'}`}
                                            title="إشارة للمستخدم"
                                        >
                                            @{user.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        className="flex-1 overflow-y-auto px-2.5 md:px-3 py-3 space-y-2.5 bg-[#efeae2] dark:bg-[#0b141a]"
                        style={{
                            backgroundImage: 'radial-gradient(circle at 18% 22%, rgba(0,0,0,0.025) 0 1px, transparent 1.5px), radial-gradient(circle at 82% 72%, rgba(0,0,0,0.025) 0 1px, transparent 1.5px)',
                            backgroundSize: '28px 28px'
                        }}
                    >
                        {messages.length === 0 && (
                            <div className="h-full flex items-center justify-center text-center text-slate-500 px-6">
                                <div className="rounded-3xl bg-white/70 backdrop-blur px-5 py-6 shadow-sm border border-white/70">
                                    <Bell className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-slate-300" />
                                    <p className="font-black text-slate-600">لا توجد رسائل حتى الآن</p>
                                    <p className="text-xs mt-1 text-slate-400">ابدأ المحادثة أو أرسل ملصقاً سريعاً.</p>
                                </div>
                            </div>
                        )}

                        {messages.map(message => {
                            const showUnreadDivider = unreadAnchorId && String(message.id) === String(unreadAnchorId);
                            const isMine = String(message.sender?.id) === String(me.id);
                            const mentionedMe = (message.mentions || []).some(item => String(item.id) === String(me.id));
                            const seenBy = getSeenBy(message, me.id);
                            const senderName = message.sender?.name || (isMine ? me.name || 'أنت' : 'مستخدم');
                            const senderAccentColor = getUserAccentColor(message.sender || {});
                            const deleted = isMessageDeleted(message, deletedMessageIds);
                            const sticker = deleted ? null : parseStickerMessage(message.text);
                            const hasAttachments = !deleted && Array.isArray(message.attachments) && message.attachments.length > 0;
                            const hasMentions = !deleted && Array.isArray(message.mentions) && message.mentions.length > 0;
                            const replyPreview = !deleted ? normalizeReplyPreview(message.replyTo) : null;
                            const isStickerOnly = Boolean(sticker && !hasAttachments && !hasMentions && !replyPreview);
                            const canDeleteForEveryone = isMine && !deleted;
                            const isDeletingThis = deletingMessageIds.has(String(message.id));
                            const seenTitle = seenBy.length > 0
                                ? `شوهد بواسطة: ${seenBy.map(user => `${user.name} - ${formatTime(user.readAt)}`).join('، ')}`
                                : 'لم تتم المشاهدة بعد';
                            const reactionGroups = getMessageReactions(message);
                            const myReactionEmoji = getMyReactionEmoji(reactionGroups, me.id);
                            const showReactionPicker = reactionTargetId === String(message.id) && !deleted;

                            return (
                                <React.Fragment key={message.id}>
                                    {showUnreadDivider && (
                                        <div className="flex items-center justify-center py-1.5">
                                            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-black shadow-sm ${isMentionedUser(message, me.id) ? 'bg-amber-300 text-amber-950' : 'bg-[#25d366] text-[#073b34] dark:text-[#d1f4cc]'}`}>
                                                {isMentionedUser(message, me.id) && <AtSign className="w-3 h-3" />}
                                                {isMentionedUser(message, me.id) ? 'إشارة إليك' : 'رسائل جديدة'}
                                            </span>
                                        </div>
                                    )}

                                    <div id={`internal-chat-message-${message.id}`} className={`flex w-full ${isMine ? 'justify-start' : 'justify-end'}`}>
                                        <div className={`flex flex-col ${isMine ? 'items-start' : 'items-end'} ${isStickerOnly ? 'max-w-[48%] md:max-w-[38%]' : 'max-w-[88%] md:max-w-[82%]'}`}>
                                            {!isMine && isStickerOnly && (
                                                <div
                                                    className="mb-1 px-1 text-[12px] md:text-[13px] font-black drop-shadow-sm"
                                                    style={{ color: senderAccentColor }}
                                                >
                                                    {senderName}
                                                </div>
                                            )}

                                            {isStickerOnly ? (
                                                <div className={`group px-1 ${isMine ? 'text-right' : 'text-left'}`}>
                                                    {isMine && (
                                                        <div className="mb-1 px-1 text-[11px] font-black text-[#075e54]/70">أنت</div>
                                                    )}
                                                    <div className={`select-none text-5xl md:text-6xl leading-none drop-shadow-sm ${sticker.effect}`} title={sticker.label}>
                                                        {sticker.emoji}
                                                    </div>
                                                    {showReactionPicker && (
                                                        <div className={`mt-1 flex items-center gap-1 rounded-full bg-white px-2 py-1 shadow-xl border border-black/10 ${isMine ? 'justify-start' : 'justify-end'}`} dir="ltr">
                                                            {REACTION_CHOICES.map(reaction => (
                                                                <button
                                                                    key={reaction}
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleMessageReaction(message, reaction);
                                                                    }}
                                                                    className="w-8 h-8 rounded-full text-xl hover:bg-[#e7fce3] hover:scale-110 transition"
                                                                    title="تفاعل"
                                                                >
                                                                    {reaction}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className={`mt-1 flex items-center gap-1 text-[10px] text-slate-500 ${isMine ? 'justify-start' : 'justify-end'}`}>
                                                        {!deleted && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setReactionTargetId(prev => prev === String(message.id) ? null : String(message.id));
                                                                }}
                                                                className={`inline-flex items-center rounded-full px-1.5 py-0.5 font-black hover:bg-white/80 ${myReactionEmoji ? 'text-[#075e54] dark:text-[#25d366]' : 'text-slate-500'}`}
                                                                title="تفاعل مع الرسالة"
                                                            >
                                                                {myReactionEmoji || '☺'}
                                                            </button>
                                                        )}
                                                        {!deleted && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startReplyToMessage(message);
                                                                }}
                                                                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-black text-slate-500 hover:bg-white/80 hover:text-[#075e54]"
                                                                title="رد على الرسالة"
                                                            >
                                                                <Reply className="w-3 h-3" />
                                                                رد
                                                            </button>
                                                        )}
                                                        {canDeleteForEveryone && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteMessageForEveryone(message);
                                                                }}
                                                                disabled={isDeletingThis}
                                                                className="inline-flex items-center rounded-full px-1 py-0.5 font-black text-slate-500 opacity-70 hover:opacity-100 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                                                title="حذف لدى الجميع"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                        <span>{formatTime(message.createdAt)}</span>
                                                        {isMine && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openSeenDetails(message);
                                                                }}
                                                                title={seenTitle}
                                                                className={`inline-flex items-center gap-1 rounded-full px-1 py-0.5 font-black hover:bg-white/80 ${seenBy.length > 0 ? 'text-[#34b7f1]' : 'text-slate-400'}`}
                                                            >
                                                                ✓✓
                                                            </button>
                                                        )}
                                                    </div>
                                                    {reactionGroups.length > 0 && (
                                                        <div className={`mt-1 flex flex-wrap gap-1 ${isMine ? 'justify-start' : 'justify-end'}`} dir="ltr">
                                                            {reactionGroups.map(group => (
                                                                <button
                                                                    key={`${message.id}-${group.emoji}`}
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleMessageReaction(message, group.emoji);
                                                                    }}
                                                                    className="inline-flex items-center gap-1 rounded-full bg-white/90 border border-black/10 px-1.5 py-0.5 text-[11px] shadow-sm hover:bg-[#e7fce3]"
                                                                    title={group.users.map(user => user.name).join('، ')}
                                                                >
                                                                    <span>{group.emoji}</span>
                                                                    <span className="font-black text-slate-600">{group.users.length}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className={`group relative rounded-2xl px-3 md:px-3.5 py-2 md:py-2.5 shadow-sm ${message.systemType === 'error' ? 'bg-red-50 text-red-950 rounded-br-sm ring-2 ring-red-200' : message.systemType === 'warning' ? 'bg-amber-50 text-amber-950 rounded-br-sm ring-2 ring-amber-200' : message.system ? 'bg-blue-50 text-blue-950 rounded-br-sm ring-2 ring-blue-200' : isMine ? 'bg-[#dcf8c6] dark:bg-[#005c4b] text-slate-900 rounded-bl-sm' : mentionedMe ? 'bg-[#fff3cd] dark:bg-amber-900/40 text-slate-900 rounded-br-sm ring-2 ring-amber-200/70' : 'bg-white text-slate-900 rounded-br-sm'}`}>
                                                    {showReactionPicker && (
                                                        <div className={`absolute -top-11 z-20 flex items-center gap-1 rounded-full bg-white px-2 py-1 shadow-xl border border-black/10 ${isMine ? 'right-0' : 'left-0'}`} dir="ltr">
                                                            {REACTION_CHOICES.map(reaction => (
                                                                <button
                                                                    key={reaction}
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleMessageReaction(message, reaction);
                                                                    }}
                                                                    className="w-8 h-8 rounded-full text-xl hover:bg-[#e7fce3] hover:scale-110 transition"
                                                                    title="تفاعل"
                                                                >
                                                                    {reaction}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {!isMine && (
                                                        <div
                                                            className="mb-1.5 text-[13px] md:text-sm font-black leading-tight tracking-tight"
                                                            style={{ color: senderAccentColor }}
                                                        >
                                                            {senderName}
                                                        </div>
                                                    )}

                                                    {isMine && (
                                                        <div className="mb-1 text-[11px] md:text-xs font-black leading-tight text-[#075e54]/75">
                                                            أنت
                                                        </div>
                                                    )}

                                                    {mentionedMe && !isMine && !deleted && (
                                                        <div className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-[10px] font-black border border-amber-200">
                                                            <AtSign className="w-3 h-3" /> تمت الإشارة إليك
                                                        </div>
                                                    )}

                                                    {replyPreview && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                scrollToMessage(replyPreview.id);
                                                            }}
                                                            className={`mb-2 block w-full rounded-xl border-r-4 bg-white/70 px-2.5 py-2 text-right shadow-sm hover:bg-white ${isMine ? 'border-[#00a884]' : 'border-slate-300'}`}
                                                            title="الانتقال للرسالة الأصلية"
                                                        >
                                                            <span className="block truncate text-[11px] font-black text-[#075e54] dark:text-[#25d366]">
                                                                رد على {String(replyPreview.sender?.id || '') === String(me.id) ? 'أنت' : replyPreview.sender?.name || 'مستخدم'}
                                                            </span>
                                                            <span className="mt-0.5 block truncate text-[12px] font-bold text-slate-600">
                                                                {replyPreview.text}
                                                            </span>
                                                        </button>
                                                    )}

                                                    {deleted ? (
                                                        <p className="whitespace-pre-wrap break-words text-[14px] md:text-[15px] leading-6 font-bold italic text-slate-500">
                                                            تم حذف هذه الرسالة
                                                        </p>
                                                    ) : (
                                                        <>
                                                            {sticker && (
                                                                <div className="inline-flex items-center gap-2 rounded-2xl bg-white/40 px-2 py-1">
                                                                    <span className={`select-none text-3xl md:text-4xl leading-none ${sticker.effect}`} title={sticker.label}>
                                                                        {sticker.emoji}
                                                                    </span>
                                                                    <span className="text-xs md:text-sm font-black text-slate-700">{sticker.label}</span>
                                                                </div>
                                                            )}

                                                            {message.text && !sticker && (
                                                                <p className="whitespace-pre-wrap break-words text-[15.5px] md:text-[16px] leading-7 font-bold text-slate-950">
                                                                    {message.text}
                                                                </p>
                                                            )}

                                                            {Array.isArray(message.mentions) && message.mentions.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mt-2">
                                                                    {message.mentions.map(item => (
                                                                        <span key={`${message.id}-${item.id}-${item.name}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black bg-[#e7fce3] dark:bg-[#005c4b] text-[#075e54] dark:text-[#25d366] border border-[#c8f7c5] dark:border-[#0a7d6b]">
                                                                            <AtSign className="w-3 h-3" /> {item.name || item.id}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                                                                <div className="mt-2 space-y-2">
                                                                    {message.attachments.map(att => {
                                                                        const fileUrl = `${baseURL}${att.url}`;
                                                                        return (
                                                                            <div key={att.id || att.fileName} className="rounded-2xl overflow-hidden border border-black/10 bg-white/70 shadow-sm">
                                                                                {isImageAttachment(att) && (
                                                                                    <a href={fileUrl} target="_blank" rel="noreferrer" className="block bg-slate-100">
                                                                                        <img src={fileUrl} alt={att.originalName || 'image'} className="w-full max-h-64 object-contain" loading="lazy" />
                                                                                    </a>
                                                                                )}
                                                                                <a
                                                                                    href={`${fileUrl}?download=1`}
                                                                                    download
                                                                                    className="flex items-center gap-2.5 p-2.5 text-xs text-slate-800 hover:bg-black/5"
                                                                                >
                                                                                    <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-[#e7fce3] dark:bg-[#005c4b] text-[#075e54] dark:text-[#25d366]">
                                                                                        {isImageAttachment(att) ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                                                                                    </span>
                                                                                    <span className="min-w-0 flex-1">
                                                                                        <span className="block truncate font-black">{att.originalName || att.fileName}</span>
                                                                                        <span className="block mt-0.5 text-[10px] font-bold text-slate-400">{formatSize(att.size)}</span>
                                                                                    </span>
                                                                                    <Download className="w-4 h-4 shrink-0" />
                                                                                </a>
                                                                                {isImageAttachment(att) && isLoggedIn && canRegisterPassportFromChat && (() => {
                                                                                    const registration = att.passportRegistration || {};
                                                                                    const registrationKey = `${message.id}:${att.id}`;
                                                                                    const locallyRegistering = registeringAttachmentKeys.has(registrationKey);
                                                                                    const isProcessing = registration.status === 'processing' || locallyRegistering;
                                                                                    const isSuccess = registration.status === 'success';
                                                                                    const isDuplicate = registration.status === 'duplicate';
                                                                                    const isFailed = registration.status === 'failed';
                                                                                    const disabled = isProcessing || isSuccess || isDuplicate;
                                                                                    const buttonLabel = isProcessing
                                                                                        ? 'جاري التسجيل...'
                                                                                        : isSuccess
                                                                                            ? `تم التسجيل${registration.officeSerial ? ` • ملف ${registration.officeSerial}` : ''}`
                                                                                            : isDuplicate
                                                                                                ? `مسجل مسبقًا${registration.officeSerial ? ` • ملف ${registration.officeSerial}` : ''}`
                                                                                                : isFailed
                                                                                                    ? 'إعادة المحاولة'
                                                                                                    : 'تسجيل';
                                                                                    const buttonClass = isSuccess
                                                                                        ? 'bg-emerald-600 text-white border-emerald-700'
                                                                                        : isDuplicate
                                                                                            ? 'bg-amber-100 text-amber-900 border-amber-300'
                                                                                            : isFailed
                                                                                                ? 'bg-red-600 text-white border-red-700 hover:bg-red-700'
                                                                                                : isProcessing
                                                                                                    ? 'bg-slate-200 text-slate-600 border-slate-300'
                                                                                                    : 'bg-[#075e54] text-white border-[#075e54] hover:bg-[#064e46]';

                                                                                    return (
                                                                                        <div className="border-t border-black/10 bg-white/80 p-2.5 space-y-2">
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.preventDefault();
                                                                                                    e.stopPropagation();
                                                                                                    registerPassportFromAttachment(message, att);
                                                                                                }}
                                                                                                disabled={disabled}
                                                                                                className={`w-full inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-black transition disabled:cursor-not-allowed ${buttonClass}`}
                                                                                                title="تسجيل بيانات الجواز من الصورة داخل صفحة الجوازات"
                                                                                            >
                                                                                                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : isSuccess ? <CheckCircle2 className="w-4 h-4" /> : isDuplicate || isFailed ? <AlertTriangle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                                                                                                {buttonLabel}
                                                                                            </button>
                                                                                            {isFailed && registration.error && (
                                                                                                <div className="flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2 py-1.5 text-[10px] font-bold text-red-700">
                                                                                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                                                                                    <span>{registration.error}</span>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}

                                                    <div className="mt-1.5 flex items-center justify-end gap-1.5 text-[10px] leading-none text-slate-500">
                                                        {!deleted && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setReactionTargetId(prev => prev === String(message.id) ? null : String(message.id));
                                                                }}
                                                                className={`inline-flex items-center rounded-full px-1.5 py-0.5 font-black opacity-80 hover:opacity-100 hover:bg-white/70 ${myReactionEmoji ? 'text-[#075e54] dark:text-[#25d366]' : 'text-slate-500'}`}
                                                                title="تفاعل مع الرسالة"
                                                            >
                                                                {myReactionEmoji || '☺'}
                                                            </button>
                                                        )}
                                                        {!deleted && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startReplyToMessage(message);
                                                                }}
                                                                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-black text-slate-500 opacity-80 hover:opacity-100 hover:bg-white/70 hover:text-[#075e54]"
                                                                title="رد على الرسالة"
                                                            >
                                                                <Reply className="w-3 h-3" />
                                                                رد
                                                            </button>
                                                        )}
                                                        {canDeleteForEveryone && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteMessageForEveryone(message);
                                                                }}
                                                                disabled={isDeletingThis}
                                                                className="ml-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-black text-slate-500 opacity-70 hover:opacity-100 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                                                title="حذف لدى الجميع"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                                {isDeletingThis ? 'جارٍ الحذف' : 'حذف'}
                                                            </button>
                                                        )}
                                                        <span>{formatTime(message.createdAt)}</span>
                                                        {isMine && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openSeenDetails(message);
                                                                }}
                                                                title={seenTitle}
                                                                className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-black hover:bg-white/70 ${seenBy.length > 0 ? 'text-[#34b7f1]' : 'text-slate-400'}`}
                                                            >
                                                                <Eye className="w-3 h-3" />
                                                                {seenBy.length > 0 ? `شوهد ${seenBy.length}` : 'لم يشاهد'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {reactionGroups.length > 0 && (
                                                        <div className="mt-1 flex flex-wrap justify-end gap-1" dir="ltr">
                                                            {reactionGroups.map(group => (
                                                                <button
                                                                    key={`${message.id}-${group.emoji}`}
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleMessageReaction(message, group.emoji);
                                                                    }}
                                                                    className="inline-flex items-center gap-1 rounded-full bg-white/80 border border-black/10 px-1.5 py-0.5 text-[11px] shadow-sm hover:bg-[#e7fce3]"
                                                                    title={group.users.map(user => user.name).join('، ')}
                                                                >
                                                                    <span>{group.emoji}</span>
                                                                    <span className="font-black text-slate-600">{group.users.length}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}

                        {typingUser && (
                            <div className="inline-flex rounded-full bg-white/75 px-3 py-1 text-[11px] text-slate-500 shadow-sm">
                                {typingUser.name} يكتب الآن...
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {mentions.length > 0 && (
                        <div className="px-3 pt-2 bg-[#f0f2f5] dark:bg-[#1f2c34] border-t border-black/5 flex flex-wrap gap-2">
                            {mentions.map(user => (
                                <span key={user.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#e7fce3] dark:bg-[#005c4b] text-[#075e54] dark:text-[#25d366] text-xs font-bold border border-[#c8f7c5] dark:border-[#0a7d6b]">
                                    @{user.name}
                                    <button type="button" onClick={() => removeMention(user.id)} className="hover:text-red-600">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {replyTo && (
                        <div className="px-3 pt-2 bg-[#f0f2f5] dark:bg-[#1f2c34] border-t border-black/5">
                            <div className="flex items-start gap-2 rounded-2xl bg-white border border-black/10 shadow-sm px-3 py-2">
                                <Reply className="w-4 h-4 mt-0.5 text-[#075e54] dark:text-[#25d366] shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[11px] font-black text-[#075e54] dark:text-[#25d366]">
                                        رد على {String(replyTo.sender?.id || '') === String(me.id) ? 'أنت' : replyTo.sender?.name || 'مستخدم'}
                                    </div>
                                    <div className="mt-0.5 truncate text-xs font-bold text-slate-600">{replyTo.text}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setReplyTo(null)}
                                    className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 hover:text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"
                                    title="إلغاء الرد"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    )}

                    {files.length > 0 && (
                        <div className="px-3 pt-2 bg-[#f0f2f5] dark:bg-[#1f2c34] border-t border-black/5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-black text-slate-600">المرفقات قبل الإرسال</span>
                                <button type="button" onClick={() => setFiles([])} className="text-xs text-red-600 font-bold flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" /> مسح الكل
                                </button>
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {files.map((file, index) => (
                                    <div key={`${file.name}-${index}`} className="shrink-0 w-28 md:w-36 rounded-2xl border border-black/10 bg-white p-2 relative shadow-sm">
                                        <button type="button" onClick={() => removeFile(index)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center shadow">
                                            <X className="w-3 h-3" />
                                        </button>
                                        <div className="text-xs font-bold text-slate-700 truncate">{file.name}</div>
                                        <div className="text-[10px] text-slate-400 mt-1">{formatSize(file.size)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="p-2 bg-[#f0f2f5] dark:bg-[#1f2c34] border-t border-black/5">
                        {showStickerPanel && (
                            <div className="mb-2 rounded-3xl border border-black/10 bg-white p-2 shadow-lg">
                                <div className="mb-2 flex items-center justify-between gap-2 px-1">
                                    <span className="text-xs font-black text-slate-700">ملصقات سريعة</span>
                                    <button
                                        type="button"
                                        onClick={() => setShowStickerPanel(false)}
                                        className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 hover:text-red-600 flex items-center justify-center"
                                        title="إغلاق الملصقات"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
                                    {CHAT_STICKERS.map(sticker => (
                                        <button
                                            key={sticker.id}
                                            type="button"
                                            onClick={() => sendSticker(sticker)}
                                            disabled={isSending}
                                            className="group rounded-2xl bg-[#f7f8fa] dark:bg-[#111b21] border border-black/5 px-1 py-2 hover:bg-[#e7fce3] hover:border-[#c8f7c5] transition-all disabled:opacity-50"
                                            title={sticker.label}
                                        >
                                            <span className="block text-2xl md:text-3xl leading-none group-hover:animate-bounce">
                                                {sticker.emoji}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex items-end gap-1.5">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(e) => {
                                    addFiles(e.target.files);
                                    e.target.value = '';
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-10 h-10 md:w-11 md:h-11 rounded-full text-[#54656f] dark:text-[#aebac1] hover:bg-black/5 flex items-center justify-center shrink-0"
                                title="إرفاق ملفات"
                            >
                                <Paperclip className="w-5 h-5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowStickerPanel(prev => !prev);
                                    resetIdleTimer();
                                }}
                                className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center shrink-0 transition-colors ${showStickerPanel ? 'bg-[#d9fdd3] dark:bg-[#005c4b] text-[#075e54] dark:text-[#25d366]' : 'text-[#54656f] dark:text-[#aebac1] hover:bg-black/5'}`}
                                title="ملصقات متحركة"
                            >
                                <span className="text-xl leading-none">😊</span>
                            </button>
                            <textarea
                                ref={textAreaRef}
                                value={input}
                                onChange={(e) => handleTyping(e.target.value)}
                                onPaste={handlePaste}
                                placeholder="رسالة"
                                rows={1}
                                className="flex-1 min-w-0 resize-none max-h-24 md:max-h-28 min-h-10 md:min-h-11 rounded-[22px] border border-transparent bg-white px-4 py-2.5 md:py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#25d366]/50"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessage();
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={sendMessage}
                                disabled={isSending || (!input.trim() && files.length === 0)}
                                className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-[#00a884] text-white hover:bg-[#029979] disabled:bg-slate-300 flex items-center justify-center shrink-0 shadow-sm"
                                title="إرسال"
                            >
                                <Send className="w-4 h-4 md:w-5 md:h-5" />
                            </button>
                        </div>
                        <p className="hidden sm:block text-[10px] text-slate-400 mt-1.5 px-12">
                            Enter للإرسال • زر رد للرد على رسالة • زر ☺ للتفاعل • زر شوهد لمعرفة القرّاء.
                        </p>
                    </div>
                </div>
            )}

            {seenDetailsMessage && (
                <div
                    className="fixed inset-0 z-[10068] flex items-end sm:items-center justify-center bg-black/30 px-3 pb-[calc(18px+env(safe-area-inset-bottom))] sm:p-4"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setSeenDetailsMessage(null);
                    }}
                >
                    <div className="w-full max-w-sm rounded-[28px] bg-white shadow-2xl border border-black/10 overflow-hidden">
                        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <h4 className="text-base font-black text-slate-900">من شاهد رسالتك؟</h4>
                                <p className="mt-0.5 text-xs font-bold text-slate-400">{formatTime(seenDetailsLiveMessage?.createdAt)}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSeenDetailsMessage(null)}
                                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-red-600 flex items-center justify-center"
                                title="إغلاق"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="max-h-72 overflow-y-auto p-3">
                            {getSeenBy(seenDetailsLiveMessage, me.id).length > 0 ? (
                                <div className="space-y-2">
                                    {getSeenBy(seenDetailsLiveMessage, me.id).map(user => (
                                        <div key={`${seenDetailsLiveMessage?.id}-${user.id}`} className="flex items-center gap-2 rounded-2xl bg-slate-50 border border-slate-100 px-3 py-2">
                                            <span
                                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-black text-white"
                                                style={{ backgroundColor: getUserAccentColor(user) }}
                                            >
                                                {String(user.name || 'م').slice(0, 1)}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-black text-slate-800">{user.name}</span>
                                                <span className="block truncate text-[11px] font-bold text-slate-400">{formatTime(user.readAt)}</span>
                                            </span>
                                            <span className="text-[#34b7f1] font-black">✓✓</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="px-4 py-8 text-center">
                                    <Eye className="mx-auto mb-2 h-9 w-9 text-slate-300" />
                                    <p className="text-sm font-black text-slate-600">لم يشاهدها أحد حتى الآن</p>
                                    <p className="mt-1 text-xs font-bold text-slate-400">ستظهر الأسماء هنا بعد فتح المستخدمين للشات.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirmMessage && (
                <div
                    className="fixed inset-0 z-[10070] flex items-end sm:items-center justify-center bg-black/35 px-3 pb-[calc(18px+env(safe-area-inset-bottom))] sm:p-4"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setDeleteConfirmMessage(null);
                    }}
                >
                    <div className="w-full max-w-sm rounded-[28px] bg-white shadow-2xl border border-black/10 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                        <div className="px-5 pt-5 pb-3 text-center">
                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <h4 className="text-base font-black text-slate-900">حذف الرسالة لدى الجميع؟</h4>
                            <p className="mt-2 text-sm leading-6 text-slate-500">
                                سيتم حذف الرسالة من المحادثة عند كل المستخدمين، ولن يظهر أي عنوان سيرفر أو IP على الموبايل.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 border-t border-slate-100 bg-slate-50 p-3">
                            <button
                                type="button"
                                onClick={() => setDeleteConfirmMessage(null)}
                                className="h-11 rounded-2xl bg-white text-slate-700 text-sm font-black border border-slate-200 hover:bg-slate-100"
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                onClick={confirmDeleteMessageForEveryone}
                                disabled={deletingMessageIds.has(String(deleteConfirmMessage?.id || ''))}
                                className="h-11 rounded-2xl bg-red-600 text-white text-sm font-black hover:bg-red-700 disabled:bg-red-300"
                            >
                                حذف
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
